import React, { useEffect, useState, useMemo } from "react";
import {
  FormControl,
  Select,
  Paragraph,
  Checkbox,
  Table,
  Button,
  Notification,
  TextInput,
  Spinner,
  Note,
  Badge,
  Menu,
  Modal,
  Textarea,
} from "@contentful/f36-components";
import { ChevronDownIcon } from "@contentful/f36-icons";
import { DialogAppSDK } from "@contentful/app-sdk";
import { createClient } from "contentful-management";
import { Environment } from "contentful-management/types";
import { translateMultipleEntries } from "../../api/backendClient";

const TRANSLATION_SERVICE_OPTIONS: string[] = [
  "Human Translation",
  "AI-Powered Translation + Post-Editing",
  "AI-Powered Translation"
];

// Backend API URL - empty string means same origin (unified server)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface DialogProps {
  sdk: DialogAppSDK;
}

// Field change within a locale
interface FieldChange {
  field: string;
  previous: any;
  current: any;
}

// Changes grouped by locale
interface LocaleChanges {
  locale: string;
  fields: FieldChange[];
}

// Single change record (one version update)
interface ChangeRecord {
  version: number;
  previousVersion: number | null;
  updatedDate: string;
  updatedBy: string;
  changes: LocaleChanges[];
}

interface TranslationStatusResult {
  entryId: string;
  title: string;
  contentType: string;
  sourceLastUpdatedOn: string;
  sourceLastUpdatedBy: string;
  status: "missing-translation" | "need-review";
  missingLanguages?: string[];
  allChanges?: ChangeRecord[];
}

const TranslationStatusReport = (props: DialogProps) => {
  const { sdk } = props;
  const cma = useMemo(() => createClient({
    apiAdapter: sdk.cmaAdapter,
  }), [sdk.cmaAdapter]);

  // Filter states
  const [sourceLanguage, setSourceLanguage] = useState(sdk.locales.default || sdk.locales.available[0]);
  const [contentTypes, setContentTypes] = useState<any[]>([]);
  const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>([]);
  const [selectAllContentTypes, setSelectAllContentTypes] = useState(false);
  const [updatedFrom, setUpdatedFrom] = useState("");
  const [updatedTo, setUpdatedTo] = useState("");

  // Data states
  const [environment, setEnvironment] = useState<Environment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [results, setResults] = useState<TranslationStatusResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [contentTypeDropdownOpen, setContentTypeDropdownOpen] = useState(false);

  // Selection states for results
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);

  // Send for Translation modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [translationService, setTranslationService] = useState(TRANSLATION_SERVICE_OPTIONS[0]);
  const [translationTitle, setTranslationTitle] = useState("");
  const [translationComments, setTranslationComments] = useState("");
  const [modalTargetLanguages, setModalTargetLanguages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Details modal states
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedEntryDetails, setSelectedEntryDetails] = useState<TranslationStatusResult | null>(null);

  // Initialize content types - use empty dependency array like NewRequest.tsx
  useEffect(() => {
    const loadData = async () => {
      try {
        const space = await cma.getSpace(sdk.ids.space);
        const env = await space.getEnvironment(sdk.ids.environment);
        setEnvironment(env);
        const types = await env.getContentTypes();
        setContentTypes(types.items);
        setDataLoaded(true);
      } catch (error) {
        console.error("Error loading content types:", error);
        setDataLoaded(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle select all content types
  const onSelectAllContentTypes = (checked: boolean) => {
    setSelectAllContentTypes(checked);
    if (checked) {
      setSelectedContentTypes(contentTypes.map((ct: any) => ct.sys.id));
    } else {
      setSelectedContentTypes([]);
    }
  };

  // Handle individual content type selection
  const onSelectContentType = (checked: boolean, contentTypeId: string) => {
    if (checked) {
      const newSelectedContentTypes = [...selectedContentTypes, contentTypeId];
      setSelectedContentTypes(newSelectedContentTypes);
      if (newSelectedContentTypes.length === contentTypes.length) {
        setSelectAllContentTypes(true);
      }
    } else {
      setSelectedContentTypes(selectedContentTypes.filter((e) => e !== contentTypeId));
      setSelectAllContentTypes(false);
    }
  };

  // Get display text for content types dropdown
  const getContentTypesDisplayText = () => {
    if (selectedContentTypes.length === 0) {
      return "Select content types...";
    }
    if (selectedContentTypes.length === contentTypes.length) {
      return "All content types selected";
    }
    if (selectedContentTypes.length <= 2) {
      return selectedContentTypes
        .map((id) => contentTypes.find((ct: any) => ct.sys.id === id)?.name)
        .filter(Boolean)
        .join(", ");
    }
    return `${selectedContentTypes.length} content types selected`;
  };

  // Handle select all results
  const onSelectAllResults = (checked: boolean) => {
    if (checked) {
      setSelectedEntries(results.map((r) => r.entryId));
    } else {
      setSelectedEntries([]);
    }
  };

  // Handle individual result selection
  const onSelectResult = (checked: boolean, entryId: string) => {
    if (checked) {
      setSelectedEntries([...selectedEntries, entryId]);
    } else {
      setSelectedEntries(selectedEntries.filter((id) => id !== entryId));
    }
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Check if string contains HTML tags
  const isHtmlContent = (value: any): boolean => {
    if (typeof value !== 'string') return false;
    return /<[a-z][\s\S]*>/i.test(value);
  };

  // Render field value - handles HTML content
  const renderFieldValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    const strValue = String(value);
    if (isHtmlContent(strValue)) {
      return <div dangerouslySetInnerHTML={{ __html: strValue }} />;
    }
    return strValue;
  };

  // Get all target languages (all languages except source)
  const getTargetLanguages = () => {
    return Object.keys(sdk.locales.names).filter((key) => key !== sourceLanguage);
  };

  // Fetch webhook logs from MongoDB
  const fetchWebhookLogs = async (): Promise<TranslationStatusResult[]> => {
    try {
      // Build query params
      const params = new URLSearchParams();
      if (selectedContentTypes.length > 0 && selectedContentTypes.length < contentTypes.length) {
        params.append('contentTypes', selectedContentTypes.join(','));
      }
      if (updatedFrom) {
        params.append('updatedFrom', updatedFrom);
      }
      if (updatedTo) {
        params.append('updatedTo', updatedTo);
      }

      const url = `${BACKEND_URL}/api/webhook/logs?${params.toString()}`;
      console.log("Fetching webhook logs from:", url);

      const response = await fetch(url);
      console.log("Webhook logs response status:", response.status);

      const data = await response.json();
      console.log("Webhook logs response data:", data);

      if (!data.success) {
        console.error("Error fetching webhook logs:", data.error);
        return [];
      }

      // Map MongoDB results to TranslationStatusResult
      return data.data.map((log: any) => ({
        entryId: log.entryId,
        title: log.entryName || log.entryId,
        contentType: log.contentType,
        sourceLastUpdatedOn: log.lastUpdatedDate,
        sourceLastUpdatedBy: log.lastUpdatedBy || "Unknown",
        status: "need-review" as const,
        allChanges: log.allChanges,
      }));
    } catch (error) {
      console.error("Error fetching webhook logs:", error);
      return [];
    }
  };

  // Validate and search
  const onSearch = async () => {
    // Prevent multiple simultaneous searches
    if (isSearching) return;

    // Validation
    if (!sourceLanguage) {
      Notification.setDuration(3000);
      Notification.error("Please select a source language.");
      return;
    }

    if (!environment) {
      Notification.setDuration(3000);
      Notification.error("Environment not loaded. Please try again.");
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    setResults([]);
    setSelectedEntries([]);

    try {
      // Get all target languages (all except source)
      const targetLanguages = getTargetLanguages();

      // Build query for Contentful
      const query: any = {
        limit: 100,
      };

      // Filter by content types if selected
      if (selectedContentTypes.length > 0 && selectedContentTypes.length < contentTypes.length) {
        query["sys.contentType.sys.id[in]"] = selectedContentTypes.join(",");
      }

      // Filter by updated date range
      if (updatedFrom) {
        query["sys.updatedAt[gte]"] = new Date(updatedFrom).toISOString();
      }
      if (updatedTo) {
        query["sys.updatedAt[lte]"] = new Date(updatedTo + "T23:59:59").toISOString();
      }

      // Fetch from both Contentful and MongoDB in parallel
      const [entries, webhookLogs] = await Promise.all([
        environment.getEntries(query),
        fetchWebhookLogs()
      ]);

      console.log("Contentful Entries:", entries);
      console.log("Webhook Logs:", webhookLogs);

      const statusResults: TranslationStatusResult[] = [];
      const resultsMap = new Map<string, number>(); // Track entry IDs and their index in results

      // Process Contentful entries (Missing Translation)
      for (const entry of entries.items) {
        // Skip if already in results (deduplication)
        if (resultsMap.has(entry.sys.id)) continue;

        // Get content type info
        const contentType = contentTypes.find(
          (ct: any) => ct.sys.id === entry.sys.contentType.sys.id
        );
        if (!contentType) continue;

        // Get display title
        let displayTitle = entry.sys.id;
        const displayField = contentType.displayField;
        if (displayField && entry.fields[displayField]) {
          displayTitle = entry.fields[displayField][sourceLanguage] ||
                         entry.fields[displayField][sdk.locales.default] ||
                         entry.sys.id;
        }

        // Get localized fields
        const localizedFields = contentType.fields
          .filter((f: any) => f.localized)
          .map((f: any) => f.id);

        if (localizedFields.length === 0) continue;

        // Check which target languages are missing translations
        const missingLanguages: string[] = [];

        for (const targetLang of targetLanguages) {
          let isMissingTranslation = false;

          for (const fieldId of localizedFields) {
            const fieldDef = entry.fields[fieldId];
            if (!fieldDef) continue;

            const sourceValue = fieldDef[sourceLanguage];
            const targetValue = fieldDef[targetLang];

            // Check if source has content
            if (sourceValue !== undefined && sourceValue !== null && sourceValue !== "") {
              // Check if target is missing or empty
              if (targetValue === undefined || targetValue === null || targetValue === "") {
                isMissingTranslation = true;
                break;
              }
            }
          }

          if (isMissingTranslation) {
            missingLanguages.push(sdk.locales.names[targetLang] || targetLang);
          }
        }

        // Only include entries with missing translations in target languages
        if (missingLanguages.length > 0) {
          // Get user info
          const updatedBy = (entry.sys as any).updatedBy;
          let updatedByEmail = "Unknown";
          if (updatedBy && updatedBy.sys && updatedBy.sys.id) {
            updatedByEmail = updatedBy.sys.id;
          }

          const index = statusResults.length;
          statusResults.push({
            entryId: entry.sys.id,
            title: displayTitle,
            contentType: contentType.name,
            sourceLastUpdatedOn: entry.sys.updatedAt,
            sourceLastUpdatedBy: updatedByEmail,
            status: "missing-translation",
            missingLanguages: missingLanguages,
          });
          resultsMap.set(entry.sys.id, index);
        }
      }

      // Add webhook logs (Need Review)
      for (const log of webhookLogs) {
        if (resultsMap.has(log.entryId)) {
          // Entry already in results (has missing translations), merge the webhook data
          const existingIndex = resultsMap.get(log.entryId)!;
          statusResults[existingIndex].allChanges = log.allChanges;
        } else {
          // Entry not in results yet, add as "need-review"
          const index = statusResults.length;
          statusResults.push(log);
          resultsMap.set(log.entryId, index);
        }
      }

      setResults(statusResults);
    } catch (error: any) {
      console.error("Search error:", error);
      Notification.setDuration(3000);
      Notification.error("An error occurred while searching: " + error.message);
    } finally {
      setIsSearching(false);
    }
  };

  const onCancel = () => {
    sdk.close();
  };

  // Open Send for Translation modal
  const onOpenTranslationModal = () => {
    setTranslationService(TRANSLATION_SERVICE_OPTIONS[0]);
    setTranslationTitle("");
    setTranslationComments("");
    setModalTargetLanguages([]); // Reset target languages
    setIsModalOpen(true);
  };

  // Close Send for Translation modal
  const onCloseTranslationModal = () => {
    setIsModalOpen(false);
    setTranslationTitle("");
    setTranslationComments("");
    setModalTargetLanguages([]);
  };

  // Handle target language selection in modal
  const onSelectModalTargetLanguage = (checked: boolean, langCode: string) => {
    if (checked) {
      if (!modalTargetLanguages.includes(langCode)) {
        setModalTargetLanguages([...modalTargetLanguages, langCode]);
      }
    } else {
      setModalTargetLanguages(modalTargetLanguages.filter((e) => e !== langCode));
    }
  };

  // Open Details modal
  const onOpenDetailsModal = (entry: TranslationStatusResult) => {
    setSelectedEntryDetails(entry);
    setIsDetailsModalOpen(true);
  };

  // Close Details modal
  const onCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedEntryDetails(null);
  };

  // Submit translation request
  const onSubmitTranslation = async () => {
    // Validations
    if (!translationTitle || !translationTitle.trim()) {
      Notification.setDuration(3000);
      Notification.error("Please specify a title for the request.");
      return;
    }

    if (!translationService) {
      Notification.setDuration(3000);
      Notification.error("Please select a translation service.");
      return;
    }

    if (!sourceLanguage) {
      Notification.setDuration(3000);
      Notification.error("Please select a source language.");
      return;
    }

    if (!modalTargetLanguages || modalTargetLanguages.length === 0) {
      Notification.setDuration(3000);
      Notification.error("Please select at least one target language.");
      return;
    }

    if (modalTargetLanguages.includes(sourceLanguage)) {
      Notification.setDuration(3000);
      Notification.error("The source language cannot be selected as a target language.");
      return;
    }

    if (selectedEntries.length === 0) {
      Notification.setDuration(3000);
      Notification.error("No entries selected.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit the job to backend using translateMultipleEntries
      const submitResult = await translateMultipleEntries(
        sdk,
        selectedEntries,
        sourceLanguage,
        modalTargetLanguages,
        translationService,
        translationTitle,
        translationComments
      );

      if (!submitResult.success || !submitResult.data?.jobId) {
        throw new Error(submitResult.error || "Failed to submit translation request");
      }

      const jobId = submitResult.data.jobId;

      // Close modal and pass jobId to parent for polling
      setIsModalOpen(false);
      setTranslationTitle("");
      setTranslationComments("");
      setModalTargetLanguages([]);
      setSelectedEntries([]);

      // Close dialog and pass jobId to parent
      sdk.close({ jobId, success: true });

    } catch (error: any) {
      console.error("Translation submission error:", error);
      Notification.setDuration(5000);
      Notification.error("Failed to submit translation request: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ width: "95%", marginLeft: "auto", marginRight: "auto", padding: "20px", textAlign: "center" }}>
        <Spinner size="large" />
        <Paragraph style={{ marginTop: "10px" }}>Loading...</Paragraph>
      </div>
    );
  }

  return (
    <div style={{ width: "95%", marginLeft: "auto", marginRight: "auto", paddingTop: "15px" }}>
      {/* Filters Section */}
      <div style={{ marginBottom: "20px" }}>
        {/* Source Language */}
        <div style={{ marginBottom: "20px" }}>
          <Paragraph>
            <b>Source Language:</b>
          </Paragraph>
          <FormControl className="f36-margin-bottom--m">
            <Select
              id="source-language"
              name="source-language"
              value={sourceLanguage}
              onChange={(e) => setSourceLanguage(e.target.value)}
            >
              {Object.keys(sdk.locales.names).map((key) => (
                <Select.Option value={key} key={"option-" + key}>
                  {sdk.locales.names[key]}
                </Select.Option>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Content Types */}
        <div style={{ marginBottom: "20px" }}>
          <Paragraph>
            <b>Content Types:</b>
          </Paragraph>
          <div style={{ width: "100%", marginTop: "8px", position: "relative" }}>
            <Menu
              isOpen={contentTypeDropdownOpen}
              onClose={() => setContentTypeDropdownOpen(false)}
            >
              <Menu.Trigger>
                <Button
                  variant="secondary"
                  endIcon={<ChevronDownIcon />}
                  onClick={() => setContentTypeDropdownOpen(!contentTypeDropdownOpen)}
                  isFullWidth
                  style={{
                    justifyContent: "space-between",
                    textAlign: "left",
                    fontWeight: "normal",
                  }}
                >
                  {getContentTypesDisplayText()}
                </Button>
              </Menu.Trigger>
              <Menu.List style={{ maxHeight: "250px", overflowY: "auto", width: "760px" }}>
              <Menu.Item
                onClick={(e:any) => {
                  e.preventDefault();
                  onSelectAllContentTypes(!selectAllContentTypes);
                }}
              >
                <Checkbox
                  id="chk-select-all-types"
                  name="chk-select-all-types"
                  isChecked={selectAllContentTypes}
                  onChange={(e:any) => {
                    e.stopPropagation();
                    onSelectAllContentTypes(e.target.checked);
                  }}
                >
                  <b>Select All</b>
                </Checkbox>
              </Menu.Item>
              <Menu.Divider />
              {contentTypes.map((ct: any) => (
                <Menu.Item
                  key={"menu-ct-" + ct.sys.id}
                  onClick={(e:any) => {
                    e.preventDefault();
                    onSelectContentType(!selectedContentTypes.includes(ct.sys.id), ct.sys.id);
                  }}
                >
                  <Checkbox
                    id={"chk-ct-" + ct.sys.id}
                    name={"chk-ct-" + ct.sys.id}
                    isChecked={selectedContentTypes.includes(ct.sys.id)}
                    onChange={(e:any) => {
                      e.stopPropagation();
                      onSelectContentType(e.target.checked, ct.sys.id);
                    }}
                  >
                    {ct.name}
                  </Checkbox>
                </Menu.Item>
              ))}
            </Menu.List>
            </Menu>
          </div>
        </div>

        {/* Date Range */}
        <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
          <div style={{ flex: 1 }}>
            <Paragraph>
              <b>Updated From:</b>
            </Paragraph>
            <TextInput
              type="date"
              value={updatedFrom}
              onChange={(e:any) => setUpdatedFrom(e.target.value)}
              className="f36-margin-top--s"
            />
          </div>
          <div style={{ flex: 1 }}>
            <Paragraph>
              <b>Updated To:</b>
            </Paragraph>
            <TextInput
              type="date"
              value={updatedTo}
              onChange={(e:any) => setUpdatedTo(e.target.value)}
              className="f36-margin-top--s"
            />
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "25px" }}>
          <Button variant="secondary" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={onSearch}
            isLoading={isSearching}
          >
            Search
          </Button>
        </div>
      </div>

      {/* Results Section */}
      {hasSearched && (
        <div style={{ marginTop: "20px" }}>
          {isSearching ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spinner size="large" />
              <Paragraph style={{ marginTop: "10px" }}>Searching...</Paragraph>
            </div>
          ) : results.length === 0 ? (
            <Note>No entries found matching the specified criteria.</Note>
          ) : (
            <>
              <Paragraph style={{ marginBottom: "10px" }}>
                <b>Found {results.length} entries</b>
                <span style={{ marginLeft: "10px", fontSize: "12px", color: "#6b7280" }}>
                  ({results.filter(r => r.status === "missing-translation").length} missing translation,
                  {" "}{results.filter(r => r.status === "need-review").length} need review)
                </span>
                {selectedEntries.length > 0 && (
                  <span> - {selectedEntries.length} selected</span>
                )}
              </Paragraph>
              <div style={{ maxHeight: "350px", overflowY: "auto" }}>
                <Table>
                  <Table.Head>
                    <Table.Row>
                      <Table.Cell style={{ width: "40px" }}>
                        <Checkbox
                          id="chk-select-all-results"
                          name="chk-select-all-results"
                          isChecked={selectedEntries.length === results.length && results.length > 0}
                          onChange={(e) => onSelectAllResults(e.target.checked)}
                        />
                      </Table.Cell>
                      <Table.Cell style={{ fontWeight: "bold" }}>Title</Table.Cell>
                      <Table.Cell style={{ fontWeight: "bold", width: "10%" }}>Type</Table.Cell>
                      <Table.Cell style={{ fontWeight: "bold", width: "12%" }}>Status</Table.Cell>
                      <Table.Cell style={{ fontWeight: "bold", width: "12%" }}>Last Updated</Table.Cell>
                      <Table.Cell style={{ fontWeight: "bold", width: "15%" }}>Info</Table.Cell>
                      <Table.Cell style={{ fontWeight: "bold", width: "8%" }}>Actions</Table.Cell>
                    </Table.Row>
                  </Table.Head>
                  <Table.Body>
                    {results.map((result) => (
                      <Table.Row key={result.entryId}>
                        <Table.Cell>
                          <Checkbox
                            id={"chk-result-" + result.entryId}
                            name={"chk-result-" + result.entryId}
                            isChecked={selectedEntries.includes(result.entryId)}
                            onChange={(e) => onSelectResult(e.target.checked, result.entryId)}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <span title={result.title} style={{
                            display: "block",
                            maxWidth: "180px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}>
                            {result.title}
                          </span>
                        </Table.Cell>
                        <Table.Cell>{result.contentType}</Table.Cell>
                        <Table.Cell>
                          <Badge variant={result.status === "missing-translation" ? "negative" : "warning"}>
                            {result.status === "missing-translation" ? "Missing Translation" : "Needs Review"}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>{formatDate(result.sourceLastUpdatedOn)}</Table.Cell>
                        <Table.Cell>
                          {result.status === "missing-translation" && result.missingLanguages && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "3px" }}>
                              {result.missingLanguages.slice(0, 3).map((lang, index) => (
                                <Badge key={index} variant="secondary" style={{ fontSize: "10px" }}>
                                  {lang}
                                </Badge>
                              ))}
                              {result.missingLanguages.length > 3 && (
                                <Badge variant="secondary" style={{ fontSize: "10px" }}>
                                  +{result.missingLanguages.length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                          {result.status === "need-review" && result.allChanges && (
                            <span style={{ fontSize: "12px", color: "#6b7280" }}>
                              {result.allChanges.length} change(s)
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {result.allChanges && result.allChanges.length > 0 && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => onOpenDetailsModal(result)}
                            >
                              Details
                            </Button>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>

              {/* Send for Translation Button */}
              {selectedEntries.length > 0 && (
                <div style={{ margin: "15px 15px", display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="positive"
                    size="small"
                    onClick={onOpenTranslationModal}
                  >
                    Send for Translation ({selectedEntries.length})
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!hasSearched && (
        <Note style={{ marginTop: "20px" }}>
          Select a source language and click Search to find entries with missing translations and entries that need review due to recent content changes.
        </Note>
      )}

      {/* Send for Translation Modal */}
      <Modal onClose={onCloseTranslationModal} isShown={isModalOpen} size="large">
        {() => (
          <>
            <Modal.Header
              title="Send for Translation"
              onClose={onCloseTranslationModal}
            />
            <Modal.Content>
              <div style={{ marginBottom: "15px" }}>
                <Paragraph style={{ marginBottom: "5px" }}>
                  <b>Selected Entries:</b> {selectedEntries.length}
                </Paragraph>
                <Paragraph>
                  <b>Source Language:</b> {sdk.locales.names[sourceLanguage] || sourceLanguage}
                </Paragraph>
              </div>

              <FormControl style={{ marginBottom: "15px" }}>
                <FormControl.Label>Translation Service</FormControl.Label>
                <Select
                  id="translation-service"
                  name="translation-service"
                  value={translationService}
                  onChange={(e: any) => setTranslationService(e.target.value)}
                >
                  {TRANSLATION_SERVICE_OPTIONS.map((option) => (
                    <Select.Option value={option} key={option}>
                      {option}
                    </Select.Option>
                  ))}
                </Select>
              </FormControl>

              <FormControl style={{ marginBottom: "15px" }}>
                <FormControl.Label>Title *</FormControl.Label>
                <TextInput
                  id="translation-title"
                  name="translation-title"
                  value={translationTitle}
                  onChange={(e: any) => setTranslationTitle(e.target.value)}
                  placeholder="Enter a title for this translation request..."
                />
              </FormControl>

              <div style={{ marginBottom: "15px" }}>
                <Paragraph style={{ marginBottom: "8px" }}>
                  <b>Target Languages: *</b>
                </Paragraph>
                <div style={{
                  maxHeight: "120px",
                  overflowY: "auto",
                  padding: "10px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px"
                }}>
                  {getTargetLanguages().map((langCode) => (
                    <div
                      key={"modal-chk-lang-" + langCode}
                      style={{
                        display: "inline-block",
                        marginRight: "20px",
                        marginBottom: "8px",
                      }}
                    >
                      <Checkbox
                        id={"modal-chk-lang-" + langCode}
                        name={"modal-chk-lang-" + langCode}
                        isChecked={modalTargetLanguages.includes(langCode)}
                        onChange={(e) => onSelectModalTargetLanguage(e.target.checked, langCode)}
                      >
                        {sdk.locales.names[langCode] || langCode}
                      </Checkbox>
                    </div>
                  ))}
                </div>
                {modalTargetLanguages.length > 0 && (
                  <Paragraph style={{ marginTop: "5px", fontSize: "12px", color: "#6b7280" }}>
                    Selected: {modalTargetLanguages.map(lang => sdk.locales.names[lang] || lang).join(", ")}
                  </Paragraph>
                )}
              </div>

              <FormControl>
                <FormControl.Label>Comments (Optional)</FormControl.Label>
                <Textarea
                  id="translation-comments"
                  name="translation-comments"
                  value={translationComments}
                  onChange={(e: any) => setTranslationComments(e.target.value)}
                  placeholder="Add any special instructions or comments for the translator..."
                  rows={3}
                />
              </FormControl>
            </Modal.Content>
            <Modal.Controls>
              <Button
                variant="secondary"
                size="small"
                onClick={onCloseTranslationModal}
              >
                Cancel
              </Button>
              <Button
                variant="positive"
                size="small"
                onClick={onSubmitTranslation}
                isLoading={isSubmitting}
              >
                Submit
              </Button>
            </Modal.Controls>
          </>
        )}
      </Modal>

      {/* Details Modal for viewing changes */}
      <Modal onClose={onCloseDetailsModal} isShown={isDetailsModalOpen} size="large">
        {() => (
          <>
            <Modal.Header
              title={`Changes for: ${selectedEntryDetails?.title || 'Entry'}`}
              onClose={onCloseDetailsModal}
            />
            <Modal.Content>
              {selectedEntryDetails?.allChanges && selectedEntryDetails.allChanges.length > 0 ? (
                <div>
                  {selectedEntryDetails.allChanges
                    .filter((changeRecord) =>
                      changeRecord.changes &&
                      changeRecord.changes.some((lc) => lc.fields && lc.fields.length > 0)
                    )
                    .map((changeRecord, recordIndex) => (
                    <div
                      key={recordIndex}
                      style={{
                        marginBottom: "20px",
                        padding: "15px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        backgroundColor: "#f9fafb"
                      }}
                    >
                      {/* Version Header */}
                      <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "15px",
                        paddingBottom: "10px",
                        borderBottom: "1px solid #e5e7eb"
                      }}>
                        <div>
                          <Badge variant="primary" style={{ marginRight: "10px" }}>
                            Version {changeRecord.version}
                          </Badge>
                          {changeRecord.previousVersion && (
                            <span style={{ fontSize: "12px", color: "#6b7280" }}>
                              (from v{changeRecord.previousVersion})
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          <span>{formatDate(changeRecord.updatedDate)}</span>
                          <span style={{ marginLeft: "10px" }}>by {changeRecord.updatedBy}</span>
                        </div>
                      </div>

                      {/* Changes by Locale */}
                      {changeRecord.changes
                        .filter((localeChange) => localeChange.fields && localeChange.fields.length > 0)
                        .map((localeChange, localeIndex) => (
                        <div key={localeIndex} style={{ marginBottom: "15px" }}>
                          <Paragraph style={{ marginBottom: "8px" }}>
                            <Badge variant="secondary">{localeChange.locale}</Badge>
                          </Paragraph>
                          <Table>
                            <Table.Head>
                              <Table.Row>
                                <Table.Cell style={{ fontWeight: "bold", width: "20%" }}>Field</Table.Cell>
                                <Table.Cell style={{ fontWeight: "bold", width: "40%" }}>Previous Value</Table.Cell>
                                <Table.Cell style={{ fontWeight: "bold", width: "40%" }}>Current Value</Table.Cell>
                              </Table.Row>
                            </Table.Head>
                            <Table.Body>
                              {localeChange.fields && localeChange.fields.map((fieldChange, fieldIndex) => (
                                <Table.Row key={fieldIndex}>
                                  <Table.Cell style={{ fontWeight: "500" }}>{fieldChange.field}</Table.Cell>
                                  <Table.Cell>
                                    <div style={{
                                      backgroundColor: "#fef2f2",
                                      padding: "8px",
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                      wordBreak: "break-word"
                                    }}>
                                      {renderFieldValue(fieldChange.previous)}
                                    </div>
                                  </Table.Cell>
                                  <Table.Cell>
                                    <div style={{
                                      backgroundColor: "#f0fdf4",
                                      padding: "8px",
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                      wordBreak: "break-word"
                                    }}>
                                      {renderFieldValue(fieldChange.current)}
                                    </div>
                                  </Table.Cell>
                                </Table.Row>
                              ))}
                            </Table.Body>
                          </Table>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <Note>No change details available for this entry.</Note>
              )}
            </Modal.Content>
            <Modal.Controls>
              <Button
                variant="secondary"
                size="small"
                onClick={onCloseDetailsModal}
              >
                Close
              </Button>
            </Modal.Controls>
          </>
        )}
      </Modal>
    </div>
  );
};

export default TranslationStatusReport;
